import React, { useState, useRef, useEffect } from "react";
import cn from "classnames";
import styles from "./MessageThread.module.sass";
import Icon from "../../Icon";

const MessageInput = ({ 
    onSendMessage,
    replyingTo,
    onCancelReply,
    editingMessage,
    onCancelEdit,
    placeholder = "Type a message...",
    maxLength = 1000,
    prefillText = "",
    onPrefillConsumed
}) => {
    const [message, setMessage] = useState("");
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const [attachments, setAttachments] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const inputRef = useRef(null);
    const fileInputRef = useRef(null);
    
    useEffect(() => {
        if (editingMessage) {
            setMessage(editingMessage.text);
            inputRef.current?.focus();
        }
    }, [editingMessage]);
    
    // Handle prefillText - append to existing message or set if empty
    useEffect(() => {
        if (prefillText) {
            setMessage(prev => {
                // If there's existing text, append with a newline; otherwise just set
                if (prev.trim()) {
                    return prev + "\n\n" + prefillText;
                }
                return prefillText;
            });
            inputRef.current?.focus();
            // Notify parent that prefill has been consumed
            if (onPrefillConsumed) {
                onPrefillConsumed();
            }
        }
    }, [prefillText, onPrefillConsumed]);
    
    const handleSubmit = (e) => {
        e.preventDefault();
        if (message.trim() || attachments.length > 0) {
            onSendMessage({
                text: message.trim(),
                attachments,
                replyTo: replyingTo,
                isEdit: !!editingMessage,
                messageId: editingMessage?.id
            });
            setMessage("");
            setAttachments([]);
            if (onCancelReply) onCancelReply();
            if (onCancelEdit) onCancelEdit();
        }
    };
    
    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSubmit(e);
        }
    };
    
    const handleFileSelect = (e) => {
        const files = Array.from(e.target.files);
        setIsUploading(true);
        
        // Process files immediately - store File objects for upload when message is sent
        // The actual upload will happen in sendMessage via streamChatService
        try {
            const newAttachments = files.map(file => ({
                id: Math.random().toString(36).substr(2, 9),
                name: file.name,
                size: file.size,
                type: file.type.startsWith('image/') ? 'image' : 'file',
                url: URL.createObjectURL(file), // Preview URL for UI
                file: file // Keep the File object for upload to Stream Chat
            }));
            setAttachments(prev => [...prev, ...newAttachments]);
        } catch (error) {
            console.error('Failed to process files:', error);
            alert('Failed to process files. Please try again.');
        } finally {
            setIsUploading(false);
            // Reset file input so same file can be selected again
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };
    
    const removeAttachment = (id) => {
        setAttachments(prev => prev.filter(a => a.id !== id));
    };
    
    const insertEmoji = (emoji) => {
        const cursor = inputRef.current?.selectionStart || message.length;
        const newMessage = message.slice(0, cursor) + emoji + message.slice(cursor);
        setMessage(newMessage);
        setShowEmojiPicker(false);
        inputRef.current?.focus();
    };
    
    // Common emojis for quick picker
    const commonEmojis = ['😊', '👍', '❤️', '😂', '🎉', '🔥', '👏', '😍', '🤔', '👌'];
    
    return (
        <div className={styles.messageInputContainer}>
            {(replyingTo || editingMessage) && (
                <div className={styles.replyBar}>
                    <div className={styles.replyInfo}>
                        <Icon name="corner-up-left" size="16" />
                        <div className={styles.replyDetails}>
                            <span className={styles.replyLabel}>
                                {editingMessage ? 'Editing message' : `Replying to ${replyingTo.sender?.name}`}
                            </span>
                            <span className={styles.replyText}>
                                {editingMessage ? editingMessage.text : replyingTo.text}
                            </span>
                        </div>
                    </div>
                    <button 
                        className={styles.cancelReply}
                        onClick={editingMessage ? onCancelEdit : onCancelReply}
                    >
                        <Icon name="close" size="16" />
                    </button>
                </div>
            )}
            
            {attachments.length > 0 && (
                <div className={styles.attachmentPreview}>
                    {attachments.map(attachment => (
                        <div key={attachment.id} className={styles.attachmentItem}>
                            {attachment.type === 'image' ? (
                                <img src={attachment.url} alt={attachment.name} />
                            ) : (
                                <div className={styles.filePreview}>
                                    <Icon name="document" size="20" />
                                    <span>{attachment.name}</span>
                                </div>
                            )}
                            <button 
                                className={styles.removeAttachment}
                                onClick={() => removeAttachment(attachment.id)}
                            >
                                <Icon name="close" size="12" />
                            </button>
                        </div>
                    ))}
                </div>
            )}
            
            <form onSubmit={handleSubmit} className={styles.inputForm}>
                <div className={styles.inputWrapper}>
                    <button
                        type="button"
                        className={styles.attachBtn}
                        onClick={() => fileInputRef.current?.click()}
                        disabled={isUploading}
                    >
                        <Icon name="paperclip" size="20" />
                    </button>
                    
                    <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        onChange={handleFileSelect}
                        style={{ display: 'none' }}
                        accept="image/*,.pdf,.doc,.docx,.txt"
                    />
                    
                    <textarea
                        ref={inputRef}
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder={placeholder}
                        className={styles.messageInput}
                        maxLength={maxLength}
                        rows="1"
                    />
                    
                    <div className={styles.inputActions}>
                        <button
                            type="button"
                            className={styles.emojiBtn}
                            onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                        >
                            <Icon name="smile" size="20" />
                        </button>
                        
                        {message.length > maxLength * 0.8 && (
                            <span className={styles.charCount}>
                                {message.length}/{maxLength}
                            </span>
                        )}
                        
                        <button
                            type="submit"
                            className={cn(styles.sendBtn, {
                                [styles.active]: message.trim() || attachments.length > 0
                            })}
                            disabled={(!message.trim() && attachments.length === 0) || isUploading}
                        >
                            <Icon name="send" size="20" />
                        </button>
                    </div>
                    
                    {showEmojiPicker && (
                        <div className={styles.emojiPicker}>
                            <div className={styles.emojiGrid}>
                                {commonEmojis.map(emoji => (
                                    <button
                                        key={emoji}
                                        type="button"
                                        className={styles.emojiBtn}
                                        onClick={() => insertEmoji(emoji)}
                                    >
                                        {emoji}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </form>
        </div>
    );
};

export default MessageInput;
